import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader, Table2, BarChart3, ChevronRight, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { paymentsAPI } from '../services/PaymentApi';

/**
 * What the money did — as figures rather than rows.
 *
 * The ledger next door answers "what was this transaction?". This answers
 * "how are we doing?", which is a different question and wants a different
 * shape: one number that matters, a trend, and where the income came from.
 *
 * Owner-agnostic like the rest of this folder: an academy and an individual
 * host ask the same questions of the same data.
 *
 * On colour — the charts render on their own neutral plane rather than the
 * themed card. The dark themes use saturated card surfaces (blue-800,
 * purple-800…), and a palette can only be validated against the surface it
 * actually sits on. Fixing the plane keeps one validated palette instead of
 * five unvalidated ones.
 */

// Validated with the dataviz palette validator against #ffffff and #1a1a19.
// Light run: adjacent CVD ΔE 9.1, normal-vision 19.6, three slots under 3:1 —
// which is why every chart here ships a legend carrying values and a table
// view, so colour never carries a number on its own.
const PALETTE = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'],
  dark:  ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300'],
};

/** Reserved status tokens — never reused as a series colour. */
const STATUS = { serious: '#ec835a', critical: '#d03b3b' };

const INK = {
  light: { surface: '#ffffff', primary: '#0b0b0b', secondary: '#52514e', muted: '#898781', grid: '#e1e0d9', axis: '#c3c2b7' },
  dark:  { surface: '#1a1a19', primary: '#ffffff', secondary: '#c3c2b7', muted: '#898781', grid: '#2c2c2a', axis: '#383835' },
};

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

/** Hero and tile values compact so they stay one line at display sizes. */
const compact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 10_000) return `$${(v / 1000).toFixed(1)}K`;
  return money(v);
};

const monthLabel = (ym, long = false) => {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString('en-US', long ? { month: 'long', year: 'numeric' } : { month: 'short' });
};

/** Presets before a custom range — nobody fights a calendar for "last 90 days". */
const RANGES = [
  { id: 'all',  label: 'All time' },
  { id: '3m',   label: 'Last 3 months', months: 3 },
  { id: '6m',   label: 'Last 6 months', months: 6 },
  { id: '12m',  label: 'Last 12 months', months: 12 },
];

const rangeToParams = (id) => {
  const preset = RANGES.find(r => r.id === id);
  if (!preset?.months) return {};
  const to = new Date();
  const from = new Date(to);
  from.setMonth(from.getMonth() - preset.months);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
};

// ── Money collected, by month ────────────────────────────────────────────────

/**
 * Columns rather than a line: months are discrete buckets, and the reader is
 * comparing their sizes, not following a continuous quantity.
 *
 * One series, so no legend — the heading already says what is plotted. Only
 * the biggest month is direct-labelled; the axis and the tooltip carry the
 * rest, because a number on every column goes unread.
 */
function MonthlyColumns({ rows, ink, accent }) {
  const [hover, setHover] = useState(null);
  const W = 640, H = 200, PAD = { t: 18, r: 12, b: 28, l: 52 };
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;

  const max = Math.max(...rows.map(r => r.earned), 1);
  // Round the axis top to something a person would say out loud.
  const step = Math.pow(10, Math.floor(Math.log10(max))) / 2 || 1;
  const top = Math.ceil(max / step) * step;
  const ticks = [0, top / 2, top];

  const band = plotW / rows.length;
  const barW = Math.min(24, band * 0.55);
  const y = (v) => PAD.t + plotH - (v / top) * plotH;
  const peak = rows.reduce((a, b) => (b.earned > a.earned ? b : a), rows[0]);

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img"
           aria-label={`Money earned by month, ${rows.length} months`}>
        {ticks.map(t => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} stroke={ink.grid} strokeWidth="1" />
            <text x={PAD.l - 8} y={y(t) + 4} textAnchor="end" fontSize="10" fill={ink.muted}
                  style={{ fontVariantNumeric: 'tabular-nums' }}>
              {t >= 1000 ? `$${(t / 1000).toFixed(0)}k` : `$${t}`}
            </text>
          </g>
        ))}
        <line x1={PAD.l} x2={W - PAD.r} y1={y(0)} y2={y(0)} stroke={ink.axis} strokeWidth="1" />

        {rows.map((r, i) => {
          const cx = PAD.l + band * i + band / 2;
          const h = Math.max((r.earned / top) * plotH, r.earned > 0 ? 2 : 0);
          const radius = Math.min(4, h);
          const x0 = cx - barW / 2, y0 = y(r.earned);
          const isPeak = r.month === peak.month && rows.length > 1;
          return (
            <g key={r.month}
               onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}
               onFocus={() => setHover(i)} onBlur={() => setHover(null)}
               tabIndex={0} style={{ outline: 'none', cursor: 'default' }}>
              {/* Hit area wider than the mark — nobody aims at a 24px column. */}
              <rect x={PAD.l + band * i} y={PAD.t} width={band} height={plotH} fill="transparent" />
              {h > 0 && (
                // 4px rounded data-end, square where it meets the baseline.
                <path
                  d={`M${x0},${y0 + h} L${x0},${y0 + radius} Q${x0},${y0} ${x0 + radius},${y0}
                      L${x0 + barW - radius},${y0} Q${x0 + barW},${y0} ${x0 + barW},${y0 + radius}
                      L${x0 + barW},${y0 + h} Z`}
                  fill={accent} opacity={hover === null || hover === i ? 1 : 0.55}
                />
              )}
              {isPeak && (
                <text x={cx} y={y0 - 6} textAnchor="middle" fontSize="10" fontWeight="600" fill={ink.secondary}>
                  {compact(r.earned)}
                </text>
              )}
              <text x={cx} y={H - 10} textAnchor="middle" fontSize="10" fill={ink.muted}>
                {monthLabel(r.month)}
              </text>
            </g>
          );
        })}
      </svg>

      {hover !== null && (
        <div className="absolute pointer-events-none px-2.5 py-1.5 rounded-lg text-xs shadow-lg"
             style={{
               background: ink.surface, color: ink.primary, border: `1px solid ${ink.grid}`,
               left: `${((PAD.l + band * hover + band / 2) / W) * 100}%`,
               top: 0, transform: 'translateX(-50%)',
             }}>
          <div className="font-bold">{money(rows[hover].earned)}</div>
          <div style={{ color: ink.secondary }}>{monthLabel(rows[hover].month, true)}</div>
          {rows[hover].paidOut > 0 && (
            <div style={{ color: ink.secondary }}>{money(rows[hover].paidOut)} paid out</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Where the income came from ───────────────────────────────────────────────

const arc = (cx, cy, rOuter, rInner, a0, a1) => {
  const p = (r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  const [x0, y0] = p(rOuter, a0), [x1, y1] = p(rOuter, a1);
  const [x2, y2] = p(rInner, a1), [x3, y3] = p(rInner, a0);
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M${x0},${y0} A${rOuter},${rOuter} 0 ${large} 1 ${x1},${y1}
          L${x2},${y2} A${rInner},${rInner} 0 ${large} 0 ${x3},${y3} Z`;
};

/**
 * A donut, because the question really is part-to-whole — which events the
 * money came from, as shares of one total.
 *
 * Below three sources it degrades to bars: a two-slice pie is a worse way of
 * showing two numbers than showing two numbers. Past six the tail folds into
 * "Other" rather than growing the palette, since a generated seventh hue is
 * indistinguishable from an existing one under colour-vision deficiency.
 */
function IncomeDonut({ slices, ink, palette }) {
  const [hover, setHover] = useState(null);
  const total = slices.reduce((s, x) => s + x.amount, 0);
  const S = 190, cx = S / 2, cy = S / 2, rOuter = 84, rInner = 52;
  const GAP = 0.03; // the 2px surface gap, in radians at this radius

  let angle = -Math.PI / 2;
  const arcs = slices.map((s, i) => {
    const sweep = (s.amount / total) * Math.PI * 2;
    const a0 = angle + GAP / 2;
    const a1 = angle + sweep - GAP / 2;
    angle += sweep;
    return { ...s, a0: Math.min(a0, a1), a1: Math.max(a0, a1), color: palette[i % palette.length] };
  });

  return (
    <div className="flex flex-wrap items-center gap-6">
      <div className="relative" style={{ width: S, height: S }}>
        <svg viewBox={`0 0 ${S} ${S}`} width={S} height={S} role="img" aria-label="Income by event">
          {arcs.map((a, i) => (
            <path key={`${a.type}:${a.id}`} d={arc(cx, cy, rOuter, rInner, a.a0, a.a1)}
                  fill={a.color} opacity={hover === null || hover === i ? 1 : 0.5}
                  onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}
                  onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                  tabIndex={0} style={{ outline: 'none' }} />
          ))}
          <text x={cx} y={cy - 2} textAnchor="middle" fontSize="17" fontWeight="700" fill={ink.primary}>
            {compact(hover === null ? total : arcs[hover].amount)}
          </text>
          <text x={cx} y={cy + 15} textAnchor="middle" fontSize="10" fill={ink.muted}>
            {hover === null ? 'collected' : `${Math.round((arcs[hover].amount / total) * 100)}% of total`}
          </text>
        </svg>
      </div>

      {/* The legend carries the values, not just the colours — three light-mode
          slots sit under 3:1, so colour alone must never be the only channel. */}
      <ul className="flex-1 min-w-[180px] space-y-1.5">
        {arcs.map((a, i) => (
          <li key={`${a.type}:${a.id}`}
              className="flex items-center gap-2 text-xs"
              onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}>
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: a.color }} />
            <span className="flex-1 truncate" style={{ color: ink.secondary }}>{a.name}</span>
            <span className="font-semibold" style={{ color: ink.primary, fontVariantNumeric: 'tabular-nums' }}>
              {money(a.amount)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Two sources is two numbers — bars say it without pretending to be a whole. */
function IncomeBars({ slices, ink, palette }) {
  const max = Math.max(...slices.map(s => s.amount), 1);
  return (
    <ul className="space-y-3">
      {slices.map((s, i) => (
        <li key={`${s.type}:${s.id}`}>
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <span className="text-xs truncate" style={{ color: ink.secondary }}>{s.name}</span>
            <span className="text-xs font-semibold" style={{ color: ink.primary, fontVariantNumeric: 'tabular-nums' }}>
              {money(s.amount)}
            </span>
          </div>
          <div className="h-2 rounded-sm" style={{ background: ink.grid }}>
            <div className="h-2 rounded-sm" style={{ width: `${(s.amount / max) * 100}%`, background: palette[i % palette.length] }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ── The view ─────────────────────────────────────────────────────────────────

export default function FinanceReports({ ownerType, ownerId }) {
  const { theme, isDark } = useTheme();
  const ink = isDark ? INK.dark : INK.light;
  const palette = isDark ? PALETTE.dark : PALETTE.light;

  const [rangeId, setRangeId] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTable, setShowTable] = useState(false);
  const [showOutstanding, setShowOutstanding] = useState(false);
  const navigate = useNavigate();

  /** Straight to the roster the money is owed on, with the tab already open. */
  const openRoster = (item) => {
    // `from` is what lets the roster offer a way back. Without it the reader
    // arrives somewhere they did not navigate to and has to find their own way
    // home through Settings.
    const back = ownerType === 'academy' ? `&from=reports&fromAcademy=${ownerId}` : '&from=reports';
    if (item.kind === 'program') {
      navigate(`/academy/${ownerId}?tab=programs&program=${item.eventId}&view=enrolled${back}`);
    } else {
      navigate(`/tournament/${item.eventId}?tab=enrolled${back}`);
    }
  };

  const load = useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    try {
      setData(await paymentsAPI.getReportSummary({ ownerType, ownerId, ...rangeToParams(rangeId) }));
    } catch (err) {
      toast.error(err?.message || 'Could not load reports');
    } finally {
      setLoading(false);
    }
  }, [ownerType, ownerId, rangeId]);

  useEffect(() => { load(); }, [load]);

  // Past six sources the tail folds into "Other" — never a seventh hue.
  const slices = useMemo(() => {
    const all = data?.byEntity || [];
    if (all.length <= 6) return all;
    const head = all.slice(0, 5);
    const tail = all.slice(5);
    return [...head, {
      type: 'other', id: 'other', name: `Other (${tail.length})`,
      amount: tail.reduce((s, e) => s + e.amount, 0),
      count: tail.reduce((s, e) => s + e.count, 0),
    }];
  }, [data]);

  const card = `rounded-xl border ${theme.border} p-5`;
  const plane = { background: ink.surface };

  if (loading && !data) {
    return <div className="flex justify-center py-16"><Loader className={`animate-spin ${theme.textMuted}`} size={22} /></div>;
  }

  const months = data?.byMonth || [];

  return (
    <div>
      <h3 className={`text-lg font-semibold ${theme.text} mb-1`}>Reports</h3>
      <p className={`text-sm ${theme.textMuted} mb-5`}>
        What you have earned, and where it came from.
      </p>

      {/* One filter row, above everything it scopes — every figure below
          re-reads against the same slice, so the numbers always agree. */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {RANGES.map(r => (
          <button
            key={r.id}
            onClick={() => setRangeId(r.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${theme.border} ${
              rangeId === r.id ? theme.buttonPrimary : `${theme.textSecondary} ${theme.cardHover}`
            }`}
          >
            {r.label}
          </button>
        ))}
        <button
          onClick={() => setShowTable(v => !v)}
          className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${theme.border} ${theme.textSecondary}`}
        >
          {showTable ? <BarChart3 size={13} /> : <Table2 size={13} />}
          {showTable ? 'Charts' : 'Table'}
        </button>
      </div>

      {/* Refetch holds the previous render rather than flashing a skeleton. */}
      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>

        {/* The hero: exactly one number the view leads with. Not range-scoped —
            it is a position, not a flow. The line beneath shows the arithmetic
            it comes from, because on its own it is indistinguishable from the
            card takings and reads as the same figure repeated. */}
        <div className={`${card} mb-4`} style={plane}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: ink.muted }}>
            To be collected from Kynaix
          </p>
          <p className="mt-1 font-semibold leading-none" style={{ color: ink.primary, fontSize: 48 }}>
            {money(data?.owed || 0)}
          </p>
          <p className="text-xs mt-2" style={{ color: ink.secondary }}>
            Card fees Kynaix collected for you ({money(data?.collectedByPlatform || 0)})
            {' '}less {money(data?.paidOut || 0)} already paid onward.
            {' '}Cash and Venmo you collected yourself never pass through Kynaix.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className={card} style={plane}>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: ink.muted }}>Earned</p>
            <p className="mt-1 text-2xl font-semibold" style={{ color: ink.primary }}>{money(data?.earned || 0)}</p>
            <p className="text-xs mt-1" style={{ color: ink.secondary }}>
              confirmed as paid, every method
            </p>
          </div>

          {/* One outstanding figure, because that is the question a host asks —
              how much is still owed to me. The split stays visible underneath
              because clearing each half is a different job: a card needs paying
              again, a cash entry needs confirming. Never range-scoped: an old
              debt does not stop existing because the reader picked a period.

              Clickable, because a total nobody can decompose is a number you
              have to take on trust. Opening it names the people it is made of
              and links to the roster where something can be done about them. */}
          <button
            onClick={() => setShowOutstanding(v => !v)}
            className={`${card} text-left w-full transition`}
            style={plane}
            aria-expanded={showOutstanding}
          >
            <p className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"
               style={{ color: ink.muted }}>
              Not yet collected
              <ChevronRight size={12} style={{ transform: showOutstanding ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
            </p>
            <p className="mt-1 text-2xl font-semibold" style={{ color: ink.primary }}>
              {money(data?.notCollected || 0)}
            </p>
            <p className="text-xs mt-1" style={{ color: ink.secondary }}>
              {money(data?.awaitingConfirmation || 0)} awaiting confirmation
              {' · '}
              {money(data?.unpaid || 0)} card not paid
              {data?.disputed?.count > 0 && (
                <>
                  {' · '}
                  <span style={{ color: STATUS.serious, fontWeight: 600 }}>
                    {data.disputed.count} disputed ({money(data.disputed.amount)})
                  </span>
                </>
              )}
            </p>
          </button>
        </div>

        {showOutstanding && (
          <OutstandingList
            ownerType={ownerType} ownerId={ownerId} ink={ink}
            onOpen={openRoster}
          />
        )}

        {showTable ? (
          <div className={card} style={plane}>
            <TableView months={months} slices={slices} ink={ink} />
          </div>
        ) : (
          <>
            <div className={`${card} mb-4`} style={plane}>
              <p className="text-sm font-bold mb-3" style={{ color: ink.primary }}>Earned by month</p>
              {months.length > 1 ? (
                <MonthlyColumns rows={months} ink={ink} accent={palette[0]} />
              ) : (
                <p className="text-xs py-6" style={{ color: ink.secondary }}>
                  {months.length === 1
                    ? `All activity so far is in ${monthLabel(months[0].month, true)} — a trend needs a second month.`
                    : 'No activity in this period.'}
                </p>
              )}
            </div>

            <div className={card} style={plane}>
              <p className="text-sm font-bold mb-3" style={{ color: ink.primary }}>Income by event</p>
              {slices.length >= 3 ? (
                <IncomeDonut slices={slices} ink={ink} palette={palette} />
              ) : slices.length > 0 ? (
                <IncomeBars slices={slices} ink={ink} palette={palette} />
              ) : (
                <p className="text-xs py-6" style={{ color: ink.secondary }}>No income in this period.</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The rows behind the outstanding total. Loaded when opened rather than with
 * the report: most readings of a report never ask this question, and the query
 * joins four tables to answer it.
 */
function OutstandingList({ ownerType, ownerId, ink, onOpen }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    paymentsAPI.getOutstanding({ ownerType, ownerId })
      .then(r => { if (live) setItems(r.items || []); })
      .catch(e => { if (live) setError(e?.message || 'Could not load the breakdown'); });
    return () => { live = false; };
  }, [ownerType, ownerId]);

  if (error) return <p className="text-xs mb-4" style={{ color: STATUS.critical }}>{error}</p>;
  if (!items) {
    return <div className="flex justify-center py-6 mb-4"><Loader className="animate-spin" size={18} style={{ color: ink.muted }} /></div>;
  }
  if (!items.length) {
    return <p className="text-xs mb-4" style={{ color: ink.secondary }}>Everything has been collected.</p>;
  }

  const label = {
    unpaid: 'Card not paid',
    pending_confirmation: 'Awaiting your confirmation',
  };

  return (
    <div className="rounded-xl border mb-4 overflow-hidden" style={{ ...{ background: ink.surface }, borderColor: ink.grid }}>
      <ul>
        {items.map(i => (
          <li key={`${i.kind}:${i.registrationId}`}>
            <button
              onClick={() => onOpen(i)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:opacity-80 transition"
              style={{ borderTop: `1px solid ${ink.grid}` }}
              title="Open the roster for this event"
            >
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold truncate" style={{ color: ink.primary }}>
                  {i.registrant}
                </span>
                <span className="block text-xs truncate" style={{ color: ink.secondary }}>
                  {i.eventName} · {label[i.paymentStatus] || i.paymentStatus}
                  {i.disputed && (
                    <span style={{ color: STATUS.serious, fontWeight: 600 }}> · disputed</span>
                  )}
                </span>
              </span>
              {i.disputed && <AlertTriangle size={13} style={{ color: STATUS.serious }} />}
              <span className="text-sm font-semibold" style={{ color: ink.primary, fontVariantNumeric: 'tabular-nums' }}>
                {money(i.amount)}
              </span>
              <ChevronRight size={14} style={{ color: ink.muted }} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The WCAG-clean twin — every plotted value reachable without hovering. */
function TableView({ months, slices, ink }) {
  const th = { color: ink.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em' };
  const td = { color: ink.primary, fontVariantNumeric: 'tabular-nums' };
  return (
    <div className="space-y-6 overflow-x-auto">
      <div>
        <p className="text-sm font-bold mb-2" style={{ color: ink.primary }}>Earned by month</p>
        <table className="w-full text-xs">
          <thead><tr>
            <th className="text-left py-1.5" style={th}>Month</th>
            <th className="text-right py-1.5" style={th}>Earned</th>
            <th className="text-right py-1.5" style={th}>Paid to you</th>
          </tr></thead>
          <tbody>
            {months.length ? months.map(m => (
              <tr key={m.month} style={{ borderTop: `1px solid ${ink.grid}` }}>
                <td className="py-1.5" style={{ color: ink.secondary }}>{monthLabel(m.month, true)}</td>
                <td className="py-1.5 text-right" style={td}>{money(m.earned)}</td>
                <td className="py-1.5 text-right" style={td}>{m.paidOut ? money(m.paidOut) : '—'}</td>
              </tr>
            )) : <tr><td className="py-2" style={{ color: ink.secondary }}>No activity.</td></tr>}
          </tbody>
        </table>
      </div>

      <div>
        <p className="text-sm font-bold mb-2" style={{ color: ink.primary }}>Income by event</p>
        <table className="w-full text-xs">
          <thead><tr>
            <th className="text-left py-1.5" style={th}>Event</th>
            <th className="text-right py-1.5" style={th}>Transactions</th>
            <th className="text-right py-1.5" style={th}>Earned</th>
          </tr></thead>
          <tbody>
            {slices.length ? slices.map(s => (
              <tr key={`${s.type}:${s.id}`} style={{ borderTop: `1px solid ${ink.grid}` }}>
                <td className="py-1.5" style={{ color: ink.secondary }}>{s.name}</td>
                <td className="py-1.5 text-right" style={td}>{s.count}</td>
                <td className="py-1.5 text-right" style={td}>{money(s.amount)}</td>
              </tr>
            )) : <tr><td className="py-2" style={{ color: ink.secondary }}>No income.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
